/**
 * Persistent per-installation identifier for the playground.
 *
 * Generates a UUIDv4 on first visit, stores it in localStorage under
 * `en-install-id`, and returns the same value on every subsequent visit
 * until the user explicitly clears it. The id is sent to the server as
 * the `sessionId` field in /playground/analyze requests so the server
 * can correlate work from the same browser across reloads, deploys, and
 * IP changes (mobile networks, VPNs, CGNAT — all of which the per-IP
 * `cid` HMAC silently merges or churns).
 *
 * Privacy:
 *   - Generated client-side, never seen by the server until you submit
 *     a request
 *   - Stored only in your browser's localStorage; cleared by the
 *     "Clear local data" button or your browser's site settings
 *   - Not used for advertising, profiling, or third-party sharing
 *   - See https://endiagram.com/privacy for the full policy
 *
 * Fallbacks:
 *   - If `crypto.randomUUID` is unavailable (very old browser), falls
 *     back to a Date.now() + Math.random() string with a `legacy-`
 *     prefix so we can distinguish in logs
 *   - If localStorage is unavailable (private browsing, storage
 *     disabled), generates an ephemeral per-page-load id with an
 *     `ephemeral-` prefix
 */

const STORAGE_KEY = 'en-install-id'

/**
 * Returns the persistent install ID for this browser, generating and
 * storing one on first call. Safe to call at module load time.
 */
export function getOrCreateClientId(): string {
    try {
        const existing = localStorage.getItem(STORAGE_KEY)
        if (existing && existing.length > 0) {
            return existing
        }
        const generated = generateUuid()
        localStorage.setItem(STORAGE_KEY, generated)
        return generated
    } catch {
        // localStorage may throw in private browsing or when storage is
        // disabled — fall through to a per-load ephemeral id.
        return `ephemeral-${Date.now()}-${Math.random().toString(36).slice(2)}`
    }
}

/**
 * Removes the install ID and any other playground-managed local state.
 * Used by the "Clear local data" button. The page should be reloaded
 * after calling this so the in-memory copies of the cleared state
 * (Zustand store, theme context) reset to defaults.
 */
export function clearLocalData(): void {
    try {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem('en-theme')
        localStorage.removeItem('en-playground')
        localStorage.removeItem('en-privacy-notice-dismissed')
    } catch {
        // Ignore — nothing useful we can do if localStorage is unreachable
    }
}

/** Generate a UUIDv4, with a fallback for older browsers. */
function generateUuid(): string {
    // crypto.randomUUID is the standard since Chrome 92, Firefox 95, Safari 15.4
    // Use the optional chain so this is safe even when crypto is undefined.
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    // Fallback: not a real UUID, but stable enough for correlation; the
    // `legacy-` prefix lets us distinguish in server logs if needed.
    return `legacy-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`
}
